'use client';

// components/Aria/hooks/useWebSpeech.ts

import { useState, useCallback, useEffect, useRef } from 'react';
import type { SpeechRecognition, SpeechRecognitionEvent, SpeechRecognitionErrorEvent } from '@/types/aria/voice';

declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

export const useWebSpeech = () => {
  const [isListening, setIsListening] = useState(false);
  const [voicesReady, setVoicesReady] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [speechSynthesisInstance, setSpeechSynthesisInstance] = useState<SpeechSynthesis | null>(null);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const isSpeakingRef = useRef(false); // To track ongoing speech
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const synth = window.speechSynthesis;
      setSpeechSynthesisInstance(synth);

      const loadVoices = () => {
        const availableVoices = synth.getVoices();
        if (availableVoices.length > 0) {
          setVoices(availableVoices);
          setVoicesReady(true);
          console.log('Voices loaded:', availableVoices.map(v => v.name));
        }
      };

      loadVoices();

      if (synth.onvoiceschanged !== undefined) {
        synth.onvoiceschanged = loadVoices;
      }

      const SpeechRecognitionConstructor = (window.SpeechRecognition || (window as any).webkitSpeechRecognition) as typeof SpeechRecognition | undefined;
      if (SpeechRecognitionConstructor) {
        const recog = new SpeechRecognitionConstructor();
        recog.continuous = false;
        recog.interimResults = false;
        recog.lang = 'en-US';
        setRecognition(recog);
      } else {
        console.warn('Speech Recognition API not supported in this browser.');
      }
    }
  }, []);

  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      setVoicesReady(true);
      
      // Comprehensive list of common female voice names
      const femaleVoiceIdentifiers = [
        'female', 'samantha', 'karen', 'victoria', 'alex', 'alice', 'amelie',
        'anna', 'elizabeth', 'ellen', 'fiona', 'laura', 'lisa', 'mary',
        'melina', 'monica', 'siri', 'tessa', 'veena', 'victoria', 'zira'
      ];
      
      // First priority: Microsoft natural female voices (typically highest quality)
      const msNaturalFemale = voices.find(voice => 
        voice.name.toLowerCase().includes('microsoft') &&
        voice.name.toLowerCase().includes('natural') &&
        femaleVoiceIdentifiers.some(id => voice.name.toLowerCase().includes(id))
      );
      
      // Second priority: Any Microsoft female voice
      const msFemale = voices.find(voice => 
        voice.name.toLowerCase().includes('microsoft') &&
        femaleVoiceIdentifiers.some(id => voice.name.toLowerCase().includes(id))
      );
      
      // Third priority: Any female voice with US English
      const femaleUsVoice = voices.find(voice => 
        voice.lang === 'en-US' && 
        femaleVoiceIdentifiers.some(id => voice.name.toLowerCase().includes(id))
      );
      
      // Fourth priority: Any female voice
      const anyFemaleVoice = voices.find(voice => 
        femaleVoiceIdentifiers.some(id => voice.name.toLowerCase().includes(id))
      );
      
      // Fallback: Any US English voice
      const usVoice = voices.find(voice => voice.lang === 'en-US');
      
      const selectedVoice = msNaturalFemale || msFemale || femaleUsVoice || anyFemaleVoice || usVoice || voices[0];
      setSelectedVoice(selectedVoice);
      
      if (selectedVoice) {
        console.log('Selected voice:', {
          name: selectedVoice.name,
          lang: selectedVoice.lang,
          isLocal: selectedVoice.localService,
          voiceURI: selectedVoice.voiceURI
        });
      }
    };

    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    loadVoices();

    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
    };
  }, []);

  const speak = useCallback((text: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!speechSynthesisInstance) {
        console.error('SpeechSynthesis is not available.');
        reject('SpeechSynthesis is not available.');
        return;
      }

      if (!voicesReady) {
        console.warn('Voices are not ready yet.');
        reject('Voices are not ready yet.');
        return;
      }

      // Cancel any ongoing speech to prevent overlapping
      if (speechSynthesisInstance.speaking) {
        console.log('Cancelling ongoing speech.');
        speechSynthesisInstance.cancel();
      }

      const utterance = new SpeechSynthesisUtterance(text.replace(/EWORLD/g, 'E World'));

      if (selectedVoice) {
        utterance.voice = selectedVoice;
        utterance.lang = selectedVoice.lang || 'en-US';
        
        // Optimize voice parameters for female voice
        if (selectedVoice.name.toLowerCase().includes('microsoft')) {
          // Microsoft voices sound better with these parameters
          utterance.pitch = 1.1;  // Slightly higher pitch
          utterance.rate = 0.95;  // Slightly slower for clarity
        } else {
          // Other voices might need different adjustments
          utterance.pitch = 1.2;  // Higher pitch for female voice
          utterance.rate = 1.0;   // Normal speed
        }
        
        // Adjust volume for clarity
        utterance.volume = 1.0;  // Maximum volume
        
        console.log(`Using voice: ${selectedVoice.name} with language ${utterance.lang}`);
        console.log(`Voice parameters: pitch=${utterance.pitch}, rate=${utterance.rate}`);
      } else {
        console.warn('Preferred voice not found. Using default voice.');
      }

      utterance.onstart = () => {
        isSpeakingRef.current = true;
        console.log('Speech synthesis started.');
      };

      utterance.onend = () => {
        isSpeakingRef.current = false;
        console.log('Speech synthesis completed.');
        resolve();
      };

      utterance.onerror = (event) => {
        isSpeakingRef.current = false;
        console.error('Speech synthesis error:', event.error);
        reject(event.error);
      };

      speechSynthesisInstance.speak(utterance);
    });
  }, [speechSynthesisInstance, selectedVoice, voicesReady]);

  const startListening = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!recognition) {
        reject('Speech recognition not available.');
        return;
      }

      if (isListening) {
        reject('Already listening.');
        return;
      }

      setIsListening(true);

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        const transcript = event.results[0][0].transcript.trim();
        console.log('Speech recognition result:', transcript);
        setIsListening(false);
        resolve(transcript);
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        reject(event.error);
      };

      recognition.onend = () => {
        if (isListening) {
          console.log('Speech recognition ended without result.');
          setIsListening(false);
          reject('Speech recognition ended without result.');
        }
      };

      try {
        recognition.start();
        console.log('Speech recognition started.');
      } catch (error) {
        console.error('Speech recognition start error:', error);
        setIsListening(false);
        reject(error);
      }
    });
  }, [recognition, isListening]);

  const stopListening = useCallback(() => {
    if (recognition && isListening) {
      recognition.stop();
      setIsListening(false);
      console.log('Speech recognition stopped.');
    }
  }, [recognition, isListening]);

  const initialize = async () => {
    if (initialized) return;

    try {
      await new Promise<void>((resolve) => {
        if (window.speechSynthesis.getVoices().length) {
          setVoicesReady(true);
          resolve();
        } else {
          window.speechSynthesis.onvoiceschanged = () => {
            setVoicesReady(true);
            resolve();
          };
        }
      });
      setInitialized(true);
      return true;
    } catch (error) {
      console.error('Failed to initialize speech synthesis:', error);
      return false;
    }
  };

  const stop = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (recognition) {
      recognition.abort();
    }
  }, [recognition]);

  return {
    speak,
    startListening,
    stopListening,
    isListening,
    voicesReady,
    initialize,
    stop
  };
};